import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import AgentToolLoadout from "../../AgentToolLoadout";
import { getMcpBrandStyle } from "../../../McpServers/mcpServerBrands";
import { resolveMcpServerBrandId } from "../../../McpServers/mcpServerTemplates";
import { filterToolCategories, formatToolNameAsLabel } from "../toolCatalog";
import { send_request } from "../../../../utils/Request";

function mcpToolKey(serverId, remoteName) {
  return `${String(serverId || "").trim()}::${String(remoteName || "").trim()}`;
}

function parseMcpToolKey(key) {
  const separator = key.indexOf("::");
  if (separator <= 0) return null;
  return {
    serverId: key.slice(0, separator),
    remoteName: key.slice(separator + 2),
  };
}

function refsToSelection(mcpServerRefs) {
  const map = new Map();
  (Array.isArray(mcpServerRefs) ? mcpServerRefs : []).forEach((ref) => {
    const serverId = String(ref?.serverId || "").trim();
    if (!serverId) return;
    const tools = Array.isArray(ref?.toolAllowlist) ? ref.toolAllowlist : [];
    map.set(serverId, new Set(tools.map((t) => String(t))));
  });
  return map;
}

function selectionToRefs(selection) {
  const refs = [];
  selection.forEach((toolSet, serverId) => {
    if (toolSet.size === 0) return;
    refs.push({ serverId, toolAllowlist: Array.from(toolSet) });
  });
  return refs;
}

function catalogToCategories(catalog) {
  return (Array.isArray(catalog) ? catalog : []).map((server) => {
    const brandId = resolveMcpServerBrandId(server);
    const brand = getMcpBrandStyle(brandId);
    return {
      title: server.name,
      color: brand.accent,
      hideCategoryConfigLabel: true,
      server,
      tools: (server.tools || []).map((tool) => ({
        name: mcpToolKey(server.serverId, tool.remoteName),
        label: formatToolNameAsLabel(tool.remoteName),
        desc: tool.description || "",
        categoryTitle: server.name,
        Icon: HubOutlinedIcon,
      })),
    };
  });
}

export default function McpToolsTab({ mcpServerRefs, setMcpServerRefs }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toolSearchQuery, setToolSearchQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await send_request(
        "ai-service/mcp/server/catalog",
        "",
        {},
        "GET"
      );
      if (cancelled) return;
      if (res?.response || res instanceof Error) {
        setError("Failed to load MCP servers.");
        setCatalog([]);
      } else {
        setCatalog(Array.isArray(res?.data) ? res.data : []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toolCategories = useMemo(
    () => catalogToCategories(catalog),
    [catalog]
  );

  const filteredToolCategories = useMemo(
    () => filterToolCategories(toolCategories, toolSearchQuery),
    [toolCategories, toolSearchQuery]
  );

  const selection = useMemo(
    () => refsToSelection(mcpServerRefs),
    [mcpServerRefs]
  );

  const enabledTools = useMemo(() => {
    const keys = [];
    selection.forEach((toolSet, serverId) => {
      toolSet.forEach((remoteName) => {
        keys.push(mcpToolKey(serverId, remoteName));
      });
    });
    return keys;
  }, [selection]);

  const commit = (nextSelection) => {
    setMcpServerRefs(selectionToRefs(nextSelection));
  };

  const toggleTool = (toolKey) => {
    const parsed = parseMcpToolKey(toolKey);
    if (!parsed) return;
    const next = new Map();
    selection.forEach((set, id) => next.set(id, new Set(set)));
    const set = next.get(parsed.serverId) || new Set();
    if (set.has(parsed.remoteName)) {
      set.delete(parsed.remoteName);
    } else {
      set.add(parsed.remoteName);
    }
    next.set(parsed.serverId, set);
    commit(next);
  };

  const setCategoryToolsEnabled = (toolNames, enabled) => {
    const next = new Map();
    selection.forEach((set, id) => next.set(id, new Set(set)));
    (Array.isArray(toolNames) ? toolNames : []).forEach((toolKey) => {
      const parsed = parseMcpToolKey(toolKey);
      if (!parsed) return;
      const toolSet = next.get(parsed.serverId) || new Set();
      if (enabled) {
        toolSet.add(parsed.remoteName);
      } else {
        toolSet.delete(parsed.remoteName);
      }
      next.set(parsed.serverId, toolSet);
    });
    commit(next);
  };

  const renderCategoryConfig = (category) => {
    const server = category.server;
    if (!server) return null;

    if (server.status === "ERROR") {
      return (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {server.lastError || "This server is in an error state."}
        </Alert>
      );
    }

    if ((server.tools || []).length === 0) {
      return (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          No tools discovered yet. Refresh the server on the{" "}
          <MuiLink href="/mcp_servers" target="_blank" rel="noopener noreferrer">
            MCP Servers
          </MuiLink>{" "}
          page.
        </Typography>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        MCP tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Equip tools from your connected MCP servers. Manage connections on the{" "}
        <MuiLink href="/mcp_servers" target="_blank" rel="noopener noreferrer">
          MCP Servers
        </MuiLink>{" "}
        page.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {catalog.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderColor: "divider",
            borderStyle: "dashed",
            borderRadius: 1,
          }}
        >
          <HubOutlinedIcon sx={{ fontSize: 36, opacity: 0.5, mb: 1 }} />
          <Typography variant="body2">
            No MCP servers connected yet. Add one on the{" "}
            <MuiLink
              href="/mcp_servers"
              target="_blank"
              rel="noopener noreferrer"
            >
              MCP Servers
            </MuiLink>{" "}
            page.
          </Typography>
        </Box>
      ) : (
        <>
          {catalog.some((server) => server.status === "ERROR") && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              One or more MCP servers need attention. Open the MCP Servers page
              to review connection errors.
            </Alert>
          )}
          <AgentToolLoadout
            toolCategories={toolCategories}
            inventoryCategories={filteredToolCategories}
            enabledTools={enabledTools}
            onToggleTool={toggleTool}
            onBulkSetToolsEnabled={setCategoryToolsEnabled}
            toolSearchQuery={toolSearchQuery}
            setToolSearchQuery={setToolSearchQuery}
            loadingToolCatalog={loading}
            includeCustomToolsCategory={false}
            renderCategoryConfig={renderCategoryConfig}
          />
        </>
      )}
    </Box>
  );
}

McpToolsTab.propTypes = {
  mcpServerRefs: PropTypes.array.isRequired,
  setMcpServerRefs: PropTypes.func.isRequired,
};
